import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Field layout ────────────────────────────────────────────────────────────
// Drawn in a 100×100 square viewBox (the minigame stage is square, so the SVG
// maps 1:1 — no distortion). Home plate sits bottom-center; the infield diamond
// is a true 45°-rotated square; the foul lines run from home along the diamond
// edges out to the foul poles; the outfield wall arcs between the poles.
const HOME   = { x: 50, y: 84 };
const POLE_L = { x: 8,  y: 42 };
const POLE_R = { x: 92, y: 42 };
const MOUND  = { x: 50, y: 68 };
// Infield bases — the diamond corners (each on a foul line).
const BASES = {
  "1B": { x: 67, y: 67 },
  "2B": { x: 50, y: 50 },
  "3B": { x: 33, y: 67 },
};
// Fielder tiles — true defensive positions inside fair territory.
const FIELDERS = [
  { pos: "P",  x: 50, y: 68 },
  { pos: "C",  x: 50, y: 91 },
  { pos: "1B", x: 64, y: 64 },
  { pos: "2B", x: 59, y: 57 },
  { pos: "SS", x: 41, y: 57 },
  { pos: "3B", x: 36, y: 64 },
  { pos: "LF", x: 27, y: 36 },
  { pos: "CF", x: 50, y: 27 },
  { pos: "RF", x: 73, y: 36 },
];

const LINE = "rgba(255,255,255,0.26)";   // foul lines
const EDGE = "rgba(255,255,255,0.42)";   // infield diamond + outfield wall
const SOFT = "rgba(255,255,255,0.28)";   // mound

// A small rotated-square base marker centered on (cx, cy).
function Base({ cx, cy }) {
  const s = 4.2;
  return (
    <rect
      x={cx - s / 2} y={cy - s / 2} width={s} height={s}
      transform={`rotate(45 ${cx} ${cy})`}
      fill="rgba(255,255,255,0.82)"
    />
  );
}

/**
 * Static ballfield backdrop for the Junk Minigames stage — purely cosmetic; the
 * chase actors render on top. Proper white-linework field: home plate, infield
 * diamond + bases + mound, foul lines fanning to a curved outfield wall, and
 * fielders on their true spots (a random sample of real players, fetched once;
 * falls back to the position label if the fetch is pending or fails).
 */
export default function MinigameField() {
  const [names, setNames] = useState({});   // position -> player name

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/players/random`)
      .then(res => {
        if (cancelled) return;
        const map = {};
        for (const p of res.data || []) {
          if (p && p.position) map[p.position] = p.name;
        }
        setNames(map);
      })
      .catch(() => { /* fall back to position labels */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="absolute inset-0 select-none pointer-events-none">
      <svg viewBox="0 0 100 100" preserveAspectRatio="xMidYMid meet"
           className="absolute inset-0 w-full h-full">
        {/* Outfield wall — a gentle arc between the two foul poles. */}
        <path d={`M ${POLE_L.x} ${POLE_L.y} Q 50 -8 ${POLE_R.x} ${POLE_R.y}`}
              fill="none" stroke={EDGE} strokeWidth="0.7" strokeLinecap="round" />

        {/* Foul lines — home plate out to each foul pole. */}
        <line x1={HOME.x} y1={HOME.y} x2={POLE_L.x} y2={POLE_L.y}
              stroke={LINE} strokeWidth="0.55" />
        <line x1={HOME.x} y1={HOME.y} x2={POLE_R.x} y2={POLE_R.y}
              stroke={LINE} strokeWidth="0.55" />

        {/* Foul-pole ticks. */}
        <line x1={POLE_L.x} y1={POLE_L.y} x2={POLE_L.x} y2={POLE_L.y - 4}
              stroke={EDGE} strokeWidth="0.7" strokeLinecap="round" />
        <line x1={POLE_R.x} y1={POLE_R.y} x2={POLE_R.x} y2={POLE_R.y - 4}
              stroke={EDGE} strokeWidth="0.7" strokeLinecap="round" />

        {/* Infield diamond — home → 1B → 2B → 3B. */}
        <polygon
          points={`${HOME.x},${HOME.y} ${BASES["1B"].x},${BASES["1B"].y} ${BASES["2B"].x},${BASES["2B"].y} ${BASES["3B"].x},${BASES["3B"].y}`}
          fill="none" stroke={EDGE} strokeWidth="0.7" strokeLinejoin="round" />

        {/* Pitcher's mound. */}
        <circle cx={MOUND.x} cy={MOUND.y} r="3.4" fill="none" stroke={SOFT} strokeWidth="0.55" />

        {/* Bases. */}
        <Base cx={BASES["1B"].x} cy={BASES["1B"].y} />
        <Base cx={BASES["2B"].x} cy={BASES["2B"].y} />
        <Base cx={BASES["3B"].x} cy={BASES["3B"].y} />

        {/* Home plate — flat-topped pentagon pointing down. */}
        <polygon
          points={`${HOME.x - 3},${HOME.y - 2.6} ${HOME.x + 3},${HOME.y - 2.6} ${HOME.x + 3},${HOME.y} ${HOME.x},${HOME.y + 2.6} ${HOME.x - 3},${HOME.y}`}
          fill="rgba(255,255,255,0.88)" />
      </svg>

      {/* Fielder tiles — random real players, or the position label as fallback. */}
      {FIELDERS.map(({ pos, x, y }) => (
        <div key={pos}
             className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center gap-0.5"
             style={{ left: `${x}%`, top: `${y}%` }}>
          <div className="w-3.5 h-3.5 rounded-full bg-slate-300/85 border border-slate-500 shadow-sm" />
          <span className="text-[8px] font-bold text-slate-400 whitespace-nowrap max-w-[88px] truncate">
            {names[pos] || pos}
          </span>
        </div>
      ))}
    </div>
  );
}
